/**
 * Language Redirect Component
 * SEO-safe language suggestion banner for Shopify stores
 *
 * @version 1.0.0
 * @author NextStep Store
 */

class LanguageRedirect {
    constructor(settings) {
        this.settings = {
            enabled: true,
            targetLocale: "en",
            bannerText:
                "Would you prefer to view this site in English?",
            switchText: "Switch to English",
            stayText: "Stay",
            bannerBgColor: "#1a1a1a",
            bannerTextColor: "#ffffff",
            autoHideDelay: 10,
            enableIpDetection: true,
            ipDetectionTimeout: 2,
            excludedCountries: ["ES"],
            autoRedirect: false,
            autoRedirectDelay: 5,
            autoRedirectText:
                "We detected that the English version will give you a better shopping experience",
            currentLocale: "es",
            availableLocales: ["en", "es"],
            ...settings,
        };

        this.storage = this.createSafeStorage();
        this.checkKey = "lang_preference_set";
        this.autoRedirectTimer = null;

        this.init();
    }

    /**
     * Initialize the language redirect system
     */
    init() {
        // Exit early if disabled
        if (!this.settings.enabled) {
            return;
        }

        // Enhanced bot detection
        if (this.isBotOrCrawler()) {
            return;
        }

        // Only run once per session
        if (this.storage.getItem(this.checkKey)) {
            return;
        }

        // Add debug function for development environments
        this.addDebugFunction();

        // Start detection process
        this.detectUserPreference()
            .then((preference) =>
                this.handleDetectionResult(preference)
            )
            .catch((error) =>
                this.handleDetectionError(error)
            );
    }

    /**
     * Enhanced bot detection with comprehensive patterns
     */
    isBotOrCrawler() {
        const userAgent =
            navigator.userAgent?.toLowerCase() || "";
        const botPatterns = [
            "googlebot",
            "bingbot",
            "slurp",
            "duckduckbot",
            "baiduspider",
            "yandexbot",
            "facebookexternalhit",
            "twitterbot",
            "linkedinbot",
            "whatsapp",
            "telegrambot",
            "applebot",
            "crawler",
            "spider",
            "bot",
            "crawl",
            "slurp",
            "search",
            "nagios",
            "phantom",
            "selenium",
            "headless",
            "lighthouse",
            "gtmetrix",
            "pagespeed",
        ];

        return (
            botPatterns.some((pattern) =>
                userAgent.includes(pattern)
            ) ||
            !navigator.userAgent ||
            navigator.webdriver === true ||
            window.callPhantom ||
            window._phantom ||
            window.__nightmare
        );
    }

    /**
     * Create safe storage wrapper with fallbacks
     */
    createSafeStorage() {
        const cookieName = "nextstep_lang_check";

        return {
            getItem: (key) => {
                try {
                    if (window.sessionStorage) {
                        return sessionStorage.getItem(key);
                    }
                } catch (e) {
                    // Fallback to cookie
                    const cookies =
                        document.cookie.split(";");
                    for (let cookie of cookies) {
                        const [name, value] = cookie
                            .trim()
                            .split("=");
                        if (
                            name ===
                            cookieName + "_" + key
                        ) {
                            return decodeURIComponent(
                                value
                            );
                        }
                    }
                }
                return null;
            },

            setItem: (key, value) => {
                try {
                    if (window.sessionStorage) {
                        sessionStorage.setItem(key, value);
                        return;
                    }
                } catch (e) {
                    // Fallback to cookie (expires in 2 hours)
                    const expires = new Date();
                    expires.setTime(
                        expires.getTime() +
                            2 * 60 * 60 * 1000
                    );
                    document.cookie = `${cookieName}_${key}=${encodeURIComponent(
                        value
                    )};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
                }
            },
        };
    }

    /**
     * Detect user's country preference using multiple methods
     */
    detectUserPreference() {
        return new Promise((resolve) => {
            // Method 1: Check timezone against excluded countries
            if (this.checkTimezone()) {
                resolve("EXCLUDED");
                return;
            }

            // Method 2: Browser language check
            if (this.checkBrowserLanguage()) {
                resolve("EXCLUDED");
                return;
            }

            // Method 3: IP-based geolocation (if enabled)
            if (this.settings.enableIpDetection) {
                this.checkIpGeolocation()
                    .then(resolve)
                    .catch(() => resolve("NON_EXCLUDED"));
            } else {
                resolve("NON_EXCLUDED");
            }
        });
    }

    /**
     * Check timezone against excluded countries
     */
    checkTimezone() {
        try {
            const timezone =
                Intl.DateTimeFormat().resolvedOptions()
                    .timeZone;

            // Map timezones to countries
            const timezoneMap = {
                "Europe/Madrid": "ES",
                "Atlantic/Canary": "ES",
                "Africa/Ceuta": "ES",
            };

            const timezoneCountry = timezoneMap[timezone];
            return (
                timezoneCountry &&
                this.settings.excludedCountries.includes(
                    timezoneCountry
                )
            );
        } catch (e) {
            return false;
        }
    }

    /**
     * Check browser language against excluded countries
     */
    checkBrowserLanguage() {
        const browserLang =
            navigator.language ||
            navigator.languages?.[0] ||
            "";

        // Check if browser language matches excluded countries
        const langCountryMap = {
            es: "ES",
            "es-ES": "ES",
        };

        const langCountry =
            langCountryMap[browserLang.toLowerCase()] ||
            browserLang.split("-")[1];
        return (
            langCountry &&
            this.settings.excludedCountries.includes(
                langCountry
            )
        );
    }

    /**
     * Check IP geolocation with timeout
     */
    checkIpGeolocation() {
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(
                () => reject(new Error("Timeout")),
                this.settings.ipDetectionTimeout * 1000
            )
        );

        const geoPromise = fetch(
            "https://ipapi.co/country_code/",
            {
                method: "GET",
                headers: { Accept: "text/plain" },
            }
        )
            .then((response) => response.text())
            .then((countryCode) => {
                const country = countryCode
                    .trim()
                    .toUpperCase();
                return this.settings.excludedCountries.includes(
                    country
                )
                    ? "EXCLUDED"
                    : "NON_EXCLUDED";
            });

        return Promise.race([geoPromise, timeoutPromise]);
    }

    /**
     * Handle detection result
     */
    handleDetectionResult(preference) {
        if (preference === "EXCLUDED") {
            // User from excluded country - no suggestion needed
            this.storage.setItem(
                this.checkKey,
                "excluded_user"
            );
        } else if (
            this.settings.currentLocale !==
            this.settings.targetLocale
        ) {
            // Non-excluded user not on target locale - show suggestion
            this.showLanguageSuggestion();
        } else {
            // Already on preferred language
            this.storage.setItem(
                this.checkKey,
                "preferred_locale"
            );
        }
    }

    /**
     * Handle detection error
     */
    handleDetectionError(error) {
        // Fallback: show suggestion for safety if error occurs
        if (
            this.settings.currentLocale !==
            this.settings.targetLocale
        ) {
            this.showLanguageSuggestion();
        }
    }

    /**
     * Show language suggestion banner
     */
    showLanguageSuggestion() {
        // Only suggest if not already on suggested locale
        if (
            this.settings.currentLocale ===
            this.settings.targetLocale
        ) {
            this.storage.setItem(
                this.checkKey,
                "same_locale"
            );
            return;
        }

        // Create banner element
        const banner = this.createBanner();

        // Add to page
        document.body.appendChild(banner);

        // Show with animation
        setTimeout(() => {
            banner.style.transform = "translateY(0)";
        }, 100);

        // Handle auto-redirect or auto-hide
        if (this.settings.autoRedirect) {
            this.startAutoRedirect(banner);
        } else {
            // Auto-hide after configured delay (passive mode)
            setTimeout(() => {
                if (banner.parentElement) {
                    banner.style.transform =
                        "translateY(-100%)";
                    setTimeout(() => banner.remove(), 300);
                }
            }, this.settings.autoHideDelay * 1000);
        }

        // Mark as shown
        this.storage.setItem(this.checkKey, "banner_shown");
    }

    /**
     * Create banner DOM element
     */
    createBanner() {
        const banner = document.createElement("div");
        banner.id = "lang-suggestion-banner";
        banner.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: ${this.settings.bannerBgColor};
      color: ${this.settings.bannerTextColor};
      padding: 12px 20px;
      font-size: 14px;
      text-align: center;
      z-index: 10000;
      border-bottom: 1px solid rgba(255,255,255,0.2);
      transform: translateY(-100%);
      transition: transform 0.3s ease;
    `;

        const suggestedUrl = this.buildSuggestedUrl();

        if (this.settings.autoRedirect) {
            // Auto-redirect mode: show countdown and stay button
            banner.innerHTML = `
        <span>${this.settings.autoRedirectText}</span>
        <span id="countdown-timer" style="margin: 0 8px; font-weight: bold;">(${this.settings.autoRedirectDelay}s)</span>
        <button onclick="window.cancelAutoRedirect()" style="margin: 0 8px; background: transparent; color: ${this.settings.bannerTextColor}; border: 1px solid ${this.settings.bannerTextColor}; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 12px;">
          ${this.settings.stayText}
        </button>
      `;
        } else {
            // Passive mode: show suggestion with both buttons
            banner.innerHTML = `
        <span>${this.settings.bannerText}</span>
        <button onclick="window.location.href='${suggestedUrl}'" style="margin: 0 8px; background: white; color: black; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 12px;">
          ${this.settings.switchText}
        </button>
        <button onclick="this.parentElement.style.display='none'" style="margin: 0 8px; background: transparent; color: ${this.settings.bannerTextColor}; border: 1px solid ${this.settings.bannerTextColor}; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 12px;">
          ${this.settings.stayText}
        </button>
      `;
        }

        return banner;
    }

    /**
     * Build suggested URL for target locale
     */
    buildSuggestedUrl() {
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search;
        let suggestedPath = currentPath;

        // Remove current locale prefix if present
        const localePattern = new RegExp(
            `^/(${this.settings.availableLocales.join(
                "|"
            )})`
        );
        suggestedPath = suggestedPath.replace(
            localePattern,
            ""
        );

        // Add target locale prefix
        suggestedPath =
            "/" +
            this.settings.targetLocale +
            (suggestedPath.startsWith("/")
                ? suggestedPath
                : "/" + suggestedPath);

        return suggestedPath + currentSearch;
    }

    /**
     * Start auto-redirect countdown
     */
    startAutoRedirect(banner) {
        let timeLeft = this.settings.autoRedirectDelay;
        const countdownElement = banner.querySelector(
            "#countdown-timer"
        );
        const suggestedUrl = this.buildSuggestedUrl();

        // Make cancelAutoRedirect available globally for the banner button
        window.cancelAutoRedirect = () => {
            this.cancelAutoRedirect(banner);
        };

        // Update countdown every second
        this.autoRedirectTimer = setInterval(() => {
            timeLeft--;
            if (countdownElement) {
                countdownElement.textContent = `(${timeLeft}s)`;
            }

            if (timeLeft <= 0) {
                clearInterval(this.autoRedirectTimer);
                window.location.href = suggestedUrl;
            }
        }, 1000);
    }

    /**
     * Cancel auto-redirect and hide banner
     */
    cancelAutoRedirect(banner) {
        if (this.autoRedirectTimer) {
            clearInterval(this.autoRedirectTimer);
            this.autoRedirectTimer = null;
        }

        if (banner && banner.parentElement) {
            banner.style.transform = "translateY(-100%)";
            setTimeout(() => banner.remove(), 300);
        }

        // Mark user preference to not show again
        this.storage.setItem(
            this.checkKey,
            "user_cancelled"
        );
    }

    /**
     * Add debug function for development environments
     */
    addDebugFunction() {
        if (
            window.location.hostname.includes(
                "localhost"
            ) ||
            window.location.hostname.includes(
                "myshopify.com"
            )
        ) {
            window.testLanguageDetection = () => {
                this.storage.setItem(this.checkKey, null);
                this.detectUserPreference().then(
                    (preference) => {
                        if (
                            preference !== "EXCLUDED" &&
                            this.settings.currentLocale !==
                                this.settings.targetLocale
                        ) {
                            this.showLanguageSuggestion();
                        }
                    }
                );
            };
        }
    }
}

// Auto-initialize if settings are available
if (
    typeof window.languageRedirectSettings !== "undefined"
) {
    new LanguageRedirect(window.languageRedirectSettings);
}
